import supabase from '../config/supabase';
import config from '../config/environment';
import { handleServiceError } from '../utils/errorHandler';
import { resolvePortalUserRoles } from '../utils/permissions';
import type { PortalRole, PortalUser, PortalUserEditData } from '../types/portalUser';
import type { PaginatedResponse } from '../types/student';
import { requestEdgeFunction } from './edgeFunctionClient';

interface RegisterUserByAdminParams {
  email: string;
  password: string;
  fullName: string;
  roles: PortalRole[];
  isActive: boolean;
}

interface RegisterUserByAdminResponse {
  success: boolean;
  message: string;
  data?: {
    user_id: string;
    full_name: string;
    roles: PortalRole[];
    is_active: boolean;
  };
  error?: string;
}

interface PortalUserByAdminResponse {
  success: boolean;
  message?: string;
  data?: PortalUserEditData & { role?: PortalRole };
  error?: string;
}

interface UpdateUserByAdminParams {
  userId: string;
  fullName: string;
  email: string;
  password?: string;
  roles: PortalRole[];
  isActive: boolean;
}

interface PortalUsersFilters {
  search?: string;
}

type PortalUsersPaginatedResponse = PaginatedResponse<PortalUser>;

class PortalUserServiceClass {
  async getByUserId(userId: string): Promise<PortalUser | null> {
    try {
      const body = await requestEdgeFunction<PortalUserByAdminResponse>('get-portal-user', {
        method: 'GET',
        query: { userId },
      });

      return body.data ? {
        id: 0,
        user_id: body.data.user_id,
        full_name: body.data.full_name,
        roles: resolvePortalUserRoles(body.data),
        is_active: body.data.is_active,
        created_at: '',
      } : null;
    } catch {
      return null;
    }
  }

  async registerByAdmin({ email, password, fullName, roles, isActive }: RegisterUserByAdminParams): Promise<string> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    if (!config.supabaseFunctionsUrl) {
      throw new Error('Configuração do Supabase Functions não encontrada.');
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/create-portal-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        email,
        password,
        full_name: fullName,
        roles,
        is_active: isActive,
      }),
    });

    const body = (await response.json().catch(() => ({}))) as Partial<RegisterUserByAdminResponse>;

    if (!response.ok || body.success === false) {
      throw new Error(body.error || body.message || 'Não foi possível cadastrar o usuário.');
    }

    return body.message || 'Usuário cadastrado com sucesso.';
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: PortalUsersFilters = {},
  ): Promise<PortalUsersPaginatedResponse> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    const url = new URL(`${config.supabaseFunctionsUrl}/list-portal-users`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));

    if (filters.search) {
      url.searchParams.set('search', filters.search);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(new Error(err?.error || 'Erro ao listar usuários do portal'), 'listar usuários do portal');
    }

    const responseBody = await response.json() as PortalUsersPaginatedResponse & {
      data?: Array<PortalUser & { role?: PortalRole }>;
    };

    return {
      ...responseBody,
      data: (responseBody.data ?? []).map((row) => ({
        ...row,
        roles: resolvePortalUserRoles(row),
      })),
    };
  }

  async getByAdmin(userId: string): Promise<PortalUserEditData> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    const url = new URL(`${config.supabaseFunctionsUrl}/get-portal-user`);
    url.searchParams.set('userId', userId);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    const body = (await response.json().catch(() => ({}))) as Partial<PortalUserByAdminResponse>;

    if (!response.ok || body.success === false || !body.data) {
      throw new Error(body.error || body.message || 'Não foi possível carregar os dados do usuário.');
    }

    return {
      ...body.data,
      roles: resolvePortalUserRoles(body.data),
    };
  }

  async updateByAdmin({ userId, fullName, email, password, roles, isActive }: UpdateUserByAdminParams): Promise<string> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (!token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/update-portal-user`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        user_id: userId,
        full_name: fullName,
        email,
        password,
        roles,
        is_active: isActive,
      }),
    });

    const body = (await response.json().catch(() => ({}))) as Partial<PortalUserByAdminResponse>;

    if (!response.ok || body.success === false) {
      throw new Error(body.error || body.message || 'Não foi possível atualizar o usuário.');
    }

    return body.message || 'Usuário atualizado com sucesso.';
  }
}

export const PortalUserService = new PortalUserServiceClass();
